import { BRAND } from "constants/brand";
import { useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

import { Form } from "react-bootstrap";

import { CSSTransition } from "react-transition-group";
import { Ring } from "@uiball/loaders";
import useUser from "hooks/useUser";
import Select from "components/Ui/SelectField/SelectField";
import { useSelector } from "react-redux";
import useArea from "hooks/useArea";
import { ROLE_LABELS } from "constants/roleLabels";

/**
 * The grantable sub-admin permissions.
 *
 * Must stay in step with `manage_*` on the API's `adminRole` model, because the
 * API enforces those columns server-side (403) whether or not this list mentions
 * them. `manage_product_videos` was missing here while `productVideo.js` already
 * required it, so the فيديوهات المنتجات page was unreachable for every
 * sub-admin and un-grantable from this form — a permission that existed only as
 * a rejection.
 *
 * NOT listed, deliberately: `manage_deliveries`. The column exists on the model
 * and is written as `false` by the admin/user creators, but nothing anywhere
 * reads it. A checkbox for it would grant exactly nothing while implying it
 * gated طلبات السائقين (which is actually under `manage_orders`). Give it a
 * checkbox when something enforces it, not before.
 */
const rolesOptions = [
    { label: "ادارة الطلبات", value: "manage_orders" },
    { label: "ادارة المنتجات", value: "manage_products" },
    { label: "ادارة الحسابات", value: "manage_users" },
    { label: "ادارة المدن", value: "manage_cities" },
    { label: "ادارة الفئات", value: "manage_categories" },
    { label: "ادارة المطاعم", value: "manage_vendors" },
    { label: "ادارة التقييمات", value: "manage_reviews" },
    { label: "ادارة عروض التوصيل", value: "manage_delivery_offers" },
    { label: "ادارة فيديوهات المنتجات", value: "manage_product_videos" },
];

const AddUser = (props) => {
    const form = useRef();
    const nodeRef = useRef();
    const { loading, addAdmin, addDelivery, error } = useUser();
    const [roles, setRoles] = useState([]);
    const [role, setRole] = useState("");
    const adminInfo = useSelector((state) => state.auth.userData);
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [city, selectCity] = useState("");
    const { areas } = useArea();
    const selectedRole = props.fixedRole || role;
    // The page fixes the role, so the form says what it is adding. A shared
    // form that says "مستخدم" on all three pages tells the operator nothing,
    // and "مستخدم" was the OLD word for the customer role — see
    // constants/roleLabels.js.
    const subjectLabel = ROLE_LABELS[selectedRole] || "الحساب";

    const newAreas = useMemo(
        () =>
            areas && city !== ""
                ? areas
                      .filter((a) => +a.cityId === +city)
                      .map((area) => ({ value: area.id, label: area.name }))
                : [],
        [areas, city],
    );

    const handleChange = (selectedOptions) => {
        setRoles(selectedOptions);
    };

    const handleChangeAreas = (selectedOptions) => {
        setSelectedAreas(selectedOptions);
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        const userData = new FormData();
        const fields = form.current.elements;
        const formRole = props.fixedRole || fields.type.value;

        selectedAreas.forEach((area) => {
            userData.append("areas[]", area.value);
        });

        if (fields.image.files[0]) {
            userData.append("image", fields.image.files[0], fields.image.files[0].name);
        }
        userData.append("name", fields.name.value);
        userData.append("email", fields.email.value);
        userData.append("phone", fields.phone.value);
        if (formRole === "admin") {
            userData.append("role", formRole);
        } else if (formRole === "super_admin") {
            userData.append("role", "admin");
            userData.append("super_admin", true);
        } else {
            userData.append("role", formRole);
        }

        if (formRole === "admin") {
            userData.append("roles", JSON.stringify(roles.map((r) => r.value)));
            userData.append("cityId", fields.cityId.value);
            userData.append("password", fields.password.value);
            userData.append("confirm_password", fields.confirm_password.value);
        } else if (formRole === "delivery") {
            userData.append("cityId", fields.cityId.value);
            userData.append("max_concurrent_orders", fields.max_concurrent_orders.value);
            userData.append("password", fields.password.value);
            userData.append("confirm_password", fields.confirm_password.value);
        } else {
            userData.append("cityId", fields.cityId.value);
            userData.append("password", fields.password.value);
            userData.append("confirm_password", fields.confirm_password.value);
        }

        try {
            let response = null;
            if (formRole === "admin" || formRole === "super_admin") {
                response = await addAdmin(userData);
            } else {
                response = await addDelivery(userData);
            }

            // Assuming `response` contains information to check if the operation succeeded

            if (response.user) {
                props.showMessage("success", "تمت الاضافه", "تمت الاضافه بنجاح");
                props.close(); // Uncomment if you need to close a modal or similar
            } else {
                props.showMessage(
                    "error",
                    "هناك خطأ",
                    response.response.data.message || "حدث خطأ غير متوقع",
                );
            }
        } catch (err) {
            console.error("Error adding user:", err);
            props.showMessage("error", "هناك خطأ", error || "حدث خطأ غير متوقع");
        }
    };

    return (
        <>
            <div className={`model ${props.show ? "model-show" : ""}`} onClick={props.close}>
                <CSSTransition
                    mountOnEnter
                    unmountOnExit
                    nodeRef={nodeRef}
                    in={props.show}
                    timeout={400}
                    classNames="show"
                >
                    <div className="addItem" ref={nodeRef} onClick={(e) => e.stopPropagation()}>
                        {loading ? (
                            <div className="loading-spinner center">
                                <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                            </div>
                        ) : null}
                        <div className="head center">
                            <h5>{`إضافة ${ROLE_LABELS[selectedRole] || ROLE_LABELS.admin}`}</h5>
                            <FontAwesomeIcon icon={faClose} onClick={props.close} />
                        </div>
                        <Form className="form" ref={form} onSubmit={submitHandler}>
                            <Form.Group className="mb-3" controlId="image">
                                <Form.Label>صوره {subjectLabel}</Form.Label>
                                <Form.Control
                                    name="image"
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.svg,.webp"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="name">
                                <Form.Label>
                                    اسم {subjectLabel}
                                    <span>*</span>
                                </Form.Label>
                                <Form.Control
                                    name="name"
                                    type="text"
                                    placeholder={`اسم ${subjectLabel}`}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="email">
                                <Form.Label>البريد الالكتروني</Form.Label>
                                <Form.Control name="email" type="text" placeholder="البريد" />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="phone">
                                <Form.Label>
                                    رقم الهاتف<span>*</span>
                                </Form.Label>
                                <Form.Control
                                    name="phone"
                                    type="text"
                                    placeholder="رقم الهاتف"
                                    required
                                />
                            </Form.Group>
                            {!props.fixedRole ? (
                                <Form.Group className="mb-3" controlId="type">
                                    <Form.Label>
                                        نوع الحساب<span>*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="type"
                                        required
                                        defaultValue={""}
                                        onChange={(e) => {
                                            setRole(e.target.value);
                                        }}
                                    >
                                        <option value="" disabled>
                                            اختر
                                        </option>
                                        {(
                                            props.allowedRoles || [
                                                "delivery",
                                                "admin",
                                                "super_admin",
                                            ]
                                        )
                                            .filter(
                                                (value) =>
                                                    value !== "super_admin" ||
                                                    adminInfo.super_admin,
                                            )
                                            .map((value) => (
                                                <option value={value} key={value}>
                                                    {ROLE_LABELS[value] || value}
                                                </option>
                                            ))}
                                    </Form.Select>
                                </Form.Group>
                            ) : null}
                            {selectedRole === "admin" ? (
                                <Form.Group className="mb-3" controlId="status">
                                    <Form.Label>
                                        الصلاحيات<span style={{ color: "red" }}>*</span>
                                    </Form.Label>
                                    <Select
                                        value={roles}
                                        options={rolesOptions}
                                        isMulti
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            ) : null}
                            <Form.Group className="mb-3" controlId="status">
                                <Form.Label>
                                    المدينه<span>*</span>
                                </Form.Label>
                                <Form.Select
                                    name="cityId"
                                    defaultValue=""
                                    onChange={(e) => {
                                        selectCity(e.target.value);
                                    }}
                                >
                                    <option value="" disabled>
                                        اختر المدينة
                                    </option>
                                    {props.cities?.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            {selectedRole === "delivery" ? (
                                <Form.Group className="mb-3" controlId="status">
                                    <Form.Label>
                                        المناطق<span style={{ color: "red" }}>*</span>
                                    </Form.Label>
                                    <Select
                                        value={selectedAreas}
                                        options={newAreas}
                                        placeholder={"المناطق"}
                                        isMulti
                                        onChange={handleChangeAreas}
                                    />
                                </Form.Group>
                            ) : null}
                            {selectedRole === "delivery" ? (
                                <Form.Group className="mb-3" controlId="max-concurrent-orders">
                                    <Form.Label>الحد الأقصى للطلبات المفتوحة</Form.Label>
                                    <Form.Control
                                        name="max_concurrent_orders"
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder="فارغ = بدون حد"
                                    />
                                </Form.Group>
                            ) : null}

                            <Form.Group className="mb-3" controlId="password">
                                <Form.Label>
                                    كلمه المرور<span>*</span>
                                </Form.Label>
                                <Form.Control
                                    name="password"
                                    type="password"
                                    placeholder="كلمه المرور"
                                    minLength={6}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="confirm-password">
                                <Form.Label>
                                    تأكيد كلمه المرور<span>*</span>
                                </Form.Label>
                                <Form.Control
                                    name="confirm_password"
                                    type="password"
                                    placeholder="تأكيد كلمه المرور"
                                    required
                                    minLength={6}
                                />
                            </Form.Group>
                            <div className="buttons">
                                <button className="cancel" onClick={props.close} type="button">
                                    إلغاء
                                </button>
                                <button className="button" type="submit">
                                    {`إضافة ${ROLE_LABELS[selectedRole] || ROLE_LABELS.admin}`}
                                </button>
                            </div>
                        </Form>
                    </div>
                </CSSTransition>
            </div>
        </>
    );
};
export default AddUser;
